import React, { useEffect, useState, lazy, Suspense } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import theme from "./theme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { rememberCampaignParams } from "./lib/campaignRef";
import Login from "./Login";
import LandingPage from "./LandingPage";
import Dashboard from "./Dashboard";
import CookieConsent from "./components/CookieConsent";
import { clearAuthStorage } from "./services/authService";
import { API_BASE, DEFAULT_HEADERS } from "./services/apiClient";
import Notifications from "./Notifications";
import Inbox from "./Inbox";
import Feed from "./Feed";
import { OnboardingGatePage } from "./components/surveys/OnboardingGatePage";
import { SurveyPage } from "./components/surveys/SurveyPage";
import { PublicResultsRoute } from "./components/surveys/PublicResultsRoute";
import { RequireOnboarding } from "./components/surveys/RequireOnboarding";
import { trackPageView } from "./services/trackingService";

// Code-split: only the landing page, login, dashboard and shared chrome stay in the
// main bundle. Everything else loads on first navigation to its route.
const CardView = lazy(() => import("./CardView"));
const Profile = lazy(() => import("./Profile"));
const ProfileExtern = lazy(() => import("./ProfileExtern"));
const Registration = lazy(() => import("./Registration"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const Connections = lazy(() => import("./Connections"));
const About = lazy(() => import("./About"));
const FoundingMembers = lazy(() => import("./FoundingMembers"));
const PrivacyPolicy = lazy(() => import("./legal/PrivacyPolicy"));
const Terms = lazy(() => import("./legal/Terms"));
const VerifyEmail = lazy(() => import("./VerifyEmail"));
const GwbiHub = lazy(() => import("./GwbiHub"));
const GwbiTeam = lazy(() => import("./GwbiTeam"));
const CampaignsAdmin = lazy(() => import("./CampaignsAdmin"));
const Discussions = lazy(() => import("./Discussions"));
const Share = lazy(() => import("./Share"));
const Administration = lazy(() => import("./Administration"));
const Governance = lazy(() => import("./Governance"));
const Zaragoza2025Workshop = lazy(() => import("./conferences/Zaragoza2025"));
const Valparaiso2024Workshop = lazy(() => import("./conferences/Valparaiso2024"));
const Geneva2024Workshop = lazy(() => import("./conferences/Geneva2024"));
const Connecticut2024Workshop = lazy(() => import("./conferences/Connecticut2024"));
const Mendoza2023Workshop = lazy(() => import("./conferences/Mendoza2023"));
const Lisbon2022Workshop = lazy(() => import("./conferences/Lisbon2022"));
const Capetown2025Workshop = lazy(() => import("./conferences/Capetown2025"));
const Perth2026Workshop = lazy(() => import("./conferences/Perth2026"));
const Milan2026Workshop = lazy(() => import("./conferences/Milan2026"));
const Surrey2027Workshop = lazy(() => import("./conferences/Surrey2027"));
const GWBIPeople = lazy(() => import("./GWBIPeople"));
const WineryDetail = lazy(() => import("./WineryDetail"));
const WineriesCard = lazy(() => import("./WineriesCard"));
const Tool = lazy(() => import("./Tool"));
const ToolsHub = lazy(() => import("./ToolsHub"));
const ProjectBuilder = lazy(() => import("./tool/ProjectBuilder"));
const PaperToPodcast = lazy(() => import("./tool/PaperToPodcast"));
const AskExpertTheme = lazy(() => import("./tool/AskExpertTheme"));
const AskExpertBuilder = lazy(() => import("./tool/AskExpertBuilder"));
const AskExpertDetail = lazy(() => import("./tool/AskExpertDetail"));
const MethodologyForum = lazy(() => import("./tool/MethodologyForum"));
const ReverseSeminars = lazy(() => import("./tool/ReverseSeminars"));
const EventCreator = lazy(() => import("./tool/EventCreator"));
const EventList = lazy(() => import("./events/EventList"));
const EventDetail = lazy(() => import("./events/EventDetail"));
const EventInvite = lazy(() => import("./events/EventInvite"));
const DataVizBuilder = lazy(() => import("./tool/DataVizBuilder"));
const ThemesHub = lazy(() => import("./ThemesHub"));
const RegionsHub = lazy(() => import("./RegionsHub"));
const MembersDirectory = lazy(() => import("./MembersDirectory"));
const OrganizationPage = lazy(() => import("./OrganizationPage"));
const Podcasts = lazy(() => import("./Podcasts"));
const ContactPage = lazy(() => import("./ContactPage"));
const Settings = lazy(() => import("./Settings"));
const Invite = lazy(() => import("./Invite"));
const JoinInvite = lazy(() => import("./JoinInvite"));
const MyWorld = lazy(() => import("./MyWorld"));
const Collections = lazy(() => import("./Collections"));
const SurveyBuilder = lazy(() => import("./components/surveys/SurveyBuilder"));
const SurveyManager = lazy(() => import("./components/surveys/SurveyManager"));

// Lazy so MapLibre only loads on the public map routes.
const Observatory = lazy(() => import("./Observatory"));
const WarmingStory = lazy(() => import("./stories/WarmingStory"));
const VarietyStory = lazy(() => import("./stories/VarietyStory"));
const LockinStory = lazy(() => import("./stories/LockinStory"));
const MarketsStory = lazy(() => import("./stories/MarketsStory"));
const HarvestStory = lazy(() => import("./stories/HarvestStory"));
const WineMarkets = lazy(() => import("./WineMarkets"));
const Scorecard = lazy(() => import("./Scorecard"));
const WineAtlas = lazy(() => import("./WineAtlas"));
const WineriesTable = lazy(() => import("./WineriesTable"));
const VisualisingWine = lazy(() => import("./VisualisingWine"));
const DiscoverDirectory = lazy(() => import("./discover/DiscoverDirectory"));
const DiscoverResearch = lazy(() => import("./discover/DiscoverResearch"));
const Profiles = lazy(() => import("./Profiles"));
const ExpertiseTool = lazy(() => import("./ExpertiseTool"));
const ExpertiseSearch = lazy(() => import("./ExpertiseSearch"));

// Keep a campaign's ?ref= and ?topic= from whatever page a link lands on,
// then take the token out of the address bar: a reader who shares the page
// would otherwise pass their personal token on, and the next person's
// sign-up would be credited to them. Registration and invitation pages keep
// it, because they read it themselves.
function CampaignRefCapture() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const { ref } = rememberCampaignParams(location.search);
    if (!ref || location.pathname.startsWith("/register") || location.pathname.startsWith("/join/")) return;
    const params = new URLSearchParams(location.search);
    params.delete("ref");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  const getInitialAuth = () => {
    try {
      return (
        localStorage.getItem("isAuthenticated") === "true" ||
        sessionStorage.getItem("isAuthenticated") === "true" ||
        !!(
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken")
        )
      );
    } catch {
      return false;
    }
  };

  const [isLoggedIn, setIsLoggedIn] = useState(getInitialAuth);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const syncFromStorage = () => setIsLoggedIn(getInitialAuth());
    window.addEventListener("storage", syncFromStorage);
    return () => window.removeEventListener("storage", syncFromStorage);
  }, []);

  const handleLogout = () => {
    try {
      clearAuthStorage();
      localStorage.removeItem("isAuthenticated");
      sessionStorage.removeItem("isAuthenticated");
    } catch (error) {
      console.error("Failed to clear auth storage on logout", error);
    } finally {
      setIsLoggedIn(false);
    }
  };

  const RequireAuth = ({ children }) => {
    return isLoggedIn ? children : <Navigate to="/" replace />;
  };

  const getStoredAuthToken = () =>
    localStorage.getItem("authToken") ?? sessionStorage.getItem("authToken");

  const purgeAuthState = () => {
    clearAuthStorage();
    localStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userEmail");
    sessionStorage.removeItem("userEmail");
    localStorage.removeItem("rememberedEmail");
  };

  const checkEmailVerified = async () => {
    const email =
      localStorage.getItem("userEmail") || sessionStorage.getItem("userEmail");
    const token = getStoredAuthToken();
    if (!email) return true;
    const headers = {
      ...DEFAULT_HEADERS,
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const attempts = [
      `${API_BASE}/users/email-verify/status?email=${encodeURIComponent(
        email || "",
      )}`,
      `${API_BASE}/auth/email-verify/status?email=${encodeURIComponent(
        email || "",
      )}`,
    ];
    for (const url of attempts) {
      try {
        const res = await fetch(url, { method: "GET", headers });
        if (!res.ok) continue;
        const data = await res.json().catch(() => null);
        const candidates = [
          data?.is_verified,
          data?.verified,
          data?.email_verified,
          data?.user?.is_verified,
          data?.profile?.is_verified,
        ];
        for (const value of candidates) {
          if (typeof value === "boolean") return value;
          if (value === 0 || value === 1) return Boolean(value);
          if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (["true", "verified", "yes", "1"].includes(normalized))
              return true;
            if (
              ["false", "unverified", "pending", "0", "no"].includes(normalized)
            )
              return false;
          }
        }
      } catch (err) {
        console.warn("Email verification check failed", err);
      }
    }
    return true;
  };

  useEffect(() => {
    let cancelled = false;
    const ensureVerified = async () => {
      if (!isLoggedIn) {
        if (!cancelled) setAuthReady(true);
        return;
      }
      const verified = await checkEmailVerified();
      if (!cancelled) {
        if (!verified) {
          if (
            localStorage.getItem("userEmail") ||
            sessionStorage.getItem("userEmail")
          ) {
            sessionStorage.setItem(
              "verificationError",
              "You need to verify your email first. Please check your inbox or resend the verification email.",
            );
            const storedEmail =
              localStorage.getItem("userEmail") ||
              sessionStorage.getItem("userEmail") ||
              "";
            sessionStorage.setItem("verificationEmailPending", storedEmail);
          }
          purgeAuthState();
          setIsLoggedIn(false);
        }
        setAuthReady(true);
      }
    };
    ensureVerified();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const hasVerifyToken = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has("token");
    } catch {
      return false;
    }
  };

  const forceLoginForVerification = hasVerifyToken();
  const loginElement = <Login setIsLoggedIn={setIsLoggedIn} />;

  if (!authReady && isLoggedIn) {
    return null;
  }

  return (
    <ChakraProvider theme={theme}>
      <Router>
        <PageViewTracker />
        <CampaignRefCapture />
        <Suspense fallback={null}>
        <Routes>
          <Route
            path="/"
            element={
              isLoggedIn && !forceLoginForVerification ? (
                <Navigate to="/dashboard" replace />
              ) : forceLoginForVerification ? (
                loginElement
              ) : (
                <LandingPage setIsLoggedIn={setIsLoggedIn} />
              )
            }
          />
          <Route path="/login" element={forceLoginForVerification ? loginElement : <Navigate to="/" replace />} />
          <Route
            path="/verify-email"
            element={
              !isLoggedIn || forceLoginForVerification ? (
                loginElement
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
<Route path="/register" element={<Registration />} />
<Route path="/verify-email" element={<VerifyEmail />} />
<Route path="/reset" element={<ResetPassword />} />
<Route path="/about" element={<About />} />

<Route
  path="/about/founding-members"
  element={<FoundingMembers onLogout={handleLogout} />}
/>

<Route path="/privacy" element={<PrivacyPolicy onLogout={handleLogout} />} />
<Route path="/terms" element={<Terms onLogout={handleLogout} />} />
<Route path="/gwbi" element={<GwbiHub onLogout={handleLogout} />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/observatory"
            element={
              <Suspense fallback={null}>
                <Observatory />
              </Suspense>
            }
          />
          <Route
            path="/story/warming"
            element={
              <Suspense fallback={null}>
                <WarmingStory />
              </Suspense>
            }
          />
          <Route path="/story/variety" element={<Suspense fallback={null}><VarietyStory /></Suspense>} />
          <Route path="/story/lockin" element={<Suspense fallback={null}><LockinStory /></Suspense>} />
          <Route path="/story/markets" element={<Suspense fallback={null}><MarketsStory /></Suspense>} />
          <Route path="/story/harvest" element={<Suspense fallback={null}><HarvestStory /></Suspense>} />
          <Route path="/wine-markets" element={<Suspense fallback={null}><WineMarkets /></Suspense>} />
          <Route path="/discover/directory" element={<Suspense fallback={null}><DiscoverDirectory onLogout={handleLogout} /></Suspense>} />
          <Route path="/discover/research" element={<Suspense fallback={null}><DiscoverResearch onLogout={handleLogout} /></Suspense>} />
          <Route path="/visualising-wine" element={<Suspense fallback={null}><VisualisingWine onLogout={handleLogout} /></Suspense>} />
          <Route path="/profiles" element={<Suspense fallback={null}><Profiles onLogout={handleLogout} /></Suspense>} />
          <Route path="/explore" element={<Navigate to="/visualising-wine" replace />} />
          <Route
            path="/scorecard"
            element={
              <Suspense fallback={null}>
                <Scorecard />
              </Suspense>
            }
          />
          <Route
            path="/atlas"
            element={
              <Suspense fallback={null}>
                <WineAtlas />
              </Suspense>
            }
          />
          <Route path="/results" element={<PublicResultsRoute />} />
          <Route path="/results/:year" element={<PublicResultsRoute />} />
          {/* Google winery map: every winery as a point, filter by country */}
          <Route
            path="/wineriestable"
            element={
              <Suspense fallback={null}>
                <WineriesTable onLogout={handleLogout} />
              </Suspense>
            }
          />
          <Route path="/map" element={<Navigate to="/wineriestable" replace />} />
          <Route path="/wineries/:locationId" element={<WineryDetail onLogout={handleLogout} />} />
          <Route path="/winery/new" element={<WineriesCard onLogout={handleLogout} />} />
          <Route
            path="/discussions"
            element={<Discussions onLogout={handleLogout} />}
          />
          <Route path="/share" element={<Share />} />
          {/* A member's invitation link lands here, then hands over to /register. */}
          <Route path="/join/:token" element={<JoinInvite />} />
          <Route
            path="/about/workshops/zaragoza-2025"
            element={<Zaragoza2025Workshop />}
          />
          <Route
            path="/about/workshops/valparaiso-2024"
            element={<Valparaiso2024Workshop />}
          />
          <Route
            path="/about/workshops/geneva-2024"
            element={<Geneva2024Workshop />}
          />
          <Route
            path="/about/workshops/connecticut-2024"
            element={<Connecticut2024Workshop />}
          />
          <Route
            path="/about/workshops/mendoza-2023"
            element={<Mendoza2023Workshop />}
          />
          <Route
            path="/about/workshops/lisbon-2022"
            element={<Lisbon2022Workshop />}
          />
          <Route
            path="/about/workshops/capetown-2025"
            element={<Capetown2025Workshop />}
          />
          <Route
            path="/about/workshops/perth-2026"
            element={<Perth2026Workshop />}
          />
          <Route
            path="/about/workshops/milan-2026"
            element={<Milan2026Workshop />}
          />
          <Route
  path="/about/workshops/surrey-2027"
  element={<Surrey2027Workshop />}
/>
          <Route
            path="/gwbi/people"
            element={
              <RequireAuth>
                <GWBIPeople onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/about/academic-institutions"
            element={
              <RequireAuth>
                <MembersDirectory category="academic-institutions" onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/about/wine-industry"
            element={
              <RequireAuth>
                <MembersDirectory category="wine-industry" onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/about/strategic-partners"
            element={
              <RequireAuth>
                <MembersDirectory category="strategic-partners" onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          {/* Organisation pages — prototype, served from src/data/orgPages.js */}
          <Route
            path="/org/:slug"
            element={
              <RequireAuth>
                <OrganizationPage onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/cardview"
            element={
              <RequireAuth>
                <CardView onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          {/* Notice Board — shareable short URL for the board tab of /cardview. */}
          <Route
            path="/noticeboard"
            element={<Navigate to="/cardview?tab=noticeboard" replace />}
          />

          {/* Readable profile address: /in/janerikmeidell. Same page and same
              public endpoint as the UUID form below, which keeps working. */}
          <Route
            path="/in/:publicId"
            element={<ProfileExtern onLogout={handleLogout} />}
          />
          {/* Public member profile — the link people put in an email signature.
              The API enforces profile_visibility and withholds the email, and
              the page hides member-only affordances when signed out. */}
          <Route
            path="/profileextern/:publicId"
            element={<ProfileExtern onLogout={handleLogout} />}
          />
          <Route
            path="/profileextern"
            element={
              <RequireAuth>
                <ProfileExtern onLogout={handleLogout} />
              </RequireAuth>
            }
          />

          <Route
            path="/connections"
            element={
              <RequireAuth>
                <Connections onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <RequireOnboarding>
                  <Dashboard onLogout={handleLogout} />
                </RequireOnboarding>
              </RequireAuth>
            }
          />
          <Route
            path="/tool"
            element={
              <RequireAuth>
                <Tool onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tools"
            element={
              <RequireAuth>
                <ToolsHub onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/project/new"
            element={
              <RequireAuth>
                <ProjectBuilder onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/paper-to-podcast"
            element={
              <RequireAuth>
                <PaperToPodcast onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/ask-expert/new"
            element={
              <RequireAuth>
                <AskExpertBuilder onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/ask-expert/:id"
            element={
              <RequireAuth>
                <AskExpertDetail onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/ask-expert"
            element={
              <RequireAuth>
                <AskExpertTheme onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/methodology-forum"
            element={
              <RequireAuth>
                <MethodologyForum onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/reverse-seminars/new"
            element={
              <RequireAuth>
                <ReverseSeminars onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/reverse-seminars"
            element={
              <RequireAuth>
                <ReverseSeminars onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/event-creator"
            element={
              <RequireAuth>
                <EventCreator onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/events"
            element={
              <RequireAuth>
                <EventList onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/events/invite/:token"
            element={<EventInvite />}
          />
          <Route
            path="/events/:eventId"
            element={
              <RequireAuth>
                <EventDetail onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/tool/data-viz"
            element={
              <RequireAuth>
                <DataVizBuilder onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/themes"
            element={
              <RequireAuth>
                <ThemesHub onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/regions"
            element={
              <RequireAuth>
                <RegionsHub onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <Notifications onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/podcasts"
            element={
              <RequireAuth>
                <Podcasts onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/inbox"
            element={
              <RequireAuth>
                <Inbox onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/feed"
            element={
              <RequireAuth>
                <Feed onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/my"
            element={
              <RequireAuth>
                <MyWorld onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/collections"
            element={
              <RequireAuth>
                <Collections onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/administration/campaigns"
            element={
              <RequireAuth>
                <CampaignsAdmin onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/administration"
            element={
              <RequireAuth>
                <Administration onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/governance"
            element={
              <RequireAuth>
                <Governance onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <OnboardingGatePage onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/survey/:code"
            element={
              <RequireAuth>
                <SurveyPage onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/survey/new"
            element={
              <RequireAuth>
                <SurveyBuilder onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/survey/edit/:code"
            element={
              <RequireAuth>
                <SurveyBuilder onLogout={handleLogout} editMode />
              </RequireAuth>
            }
          />
          <Route
            path="/surveys/manage"
            element={
              <RequireAuth>
                <SurveyManager onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/expertise/search"
            element={
              <RequireAuth>
                <Suspense fallback={null}>
                  <ExpertiseSearch onLogout={handleLogout} />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="/expertise"
            element={
              <RequireAuth>
                <Suspense fallback={null}>
                  <ExpertiseTool onLogout={handleLogout} />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <Settings onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="/invite"
            element={
              <RequireAuth>
                <Invite onLogout={handleLogout} />
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={<Navigate to={isLoggedIn ? "/dashboard" : "/"} replace />}
          />
        </Routes>
        </Suspense>
        <CookieConsent />
      </Router>
    </ChakraProvider>
  );
}

export default App;
